import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import {getChannel} from 'postal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import CloseIcon from '@mui/icons-material/Close';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import Typography from '@mui/material/Typography';

import { setModuleName, setPageIndex as reduxSetPageIndex, setPageName as reduxSetPageName, setSplitWindow, setSplitMode} from '../features/menu/menuSlice';

const postal = getChannel('ucs');

// var cards = [];

/*
  Card: {
     module: "moduleName", // the parent module, to resolve pages that can belong to several modules
     page: <Page/>,     // could be null if already registered
     name: 'cardName',  // required
     reuse: true/false, // if true and card already in system just switch to it, otherwise create new card and switch to it
     right: true/false, // this page is valid as right page
     permanent: true/false, // install at init time and don't unmount, for apps that can be mounted only once
     icon: <Icon/>,  // icon to put in menu 
     label: "label", // label to put in menu
     enableRightCloseButton: true/false, // add a floating close button on the right top corner to close the panel
     includeModules: [...modules], // the page modules in which this is available
     origin: 'cardName', // the name of card origin of this event
     closeCallback: () => {}, // should return a promise resolving to true (or reject meaning false) if close action should proceed or be cancelled
     closeNotify: () => {}, // invoked when window (left or right) is about to be closed, 
    }
*/
var globalCards = [];
var globalLeftCard;
var globalRightCard;
var globalCardStack = [];
var globalActiveRightCards = [];
var localSplit = false;


function CardLayout (props) {
    const {
        modules,
        ...other
    } = props;
    const [cards, setCards] = useState([]);
    const [permanentCards, setPermanentCards] = useState([]);
    const [cardMap, setCardMap] = useState({});
    const [cardStack, setCardStack] = useState([]);
    const [leftCard, setLeftCard] = useState('');
    const [rightCard, setRightCard] = useState('');
    const [split, setSplit] = useState(false); // split mode: true/false
    const [windowMenuAnchorEl, setWindowMenuAnchorEl] = useState(null);
    // const [activeRightCards, setActiveRightCards] = useState([]);  // cards that are valid to put on right split panel
    const [rightCloseEnabled, setRightCloseEnabled] = useState(false); // add a close button to right panel 
    const useSplitWindow = useSelector((state) => state.menu.useSplitWindow); // deprecated
    const splitWindowEnabled = useSelector( (state) => state.menu.splitWindowEnabled);
    const menuSplitMode = useSelector( (state) => state.menu.splitMode);
    const activeModule = useSelector( (state) => state.menu.moduleName);


    const dispatch = useDispatch();
    const openWindowMenu = Boolean(windowMenuAnchorEl);

    useEffect(() => {
        const splitSubs = postal.subscribe(
            "app.page.window.split",
            (envelope) => {
                // console.log('Got message: ' + JSON.stringify(data) + ',' + JSON.stringify(envelope));
                handlePostalSplitWindow(envelope.payload);
            }
        );
        const closeSubs = postal.subscribe(
            "app.page.window.close",
            (envelope) => {
                handlePostalCloseWindow(envelope.payload);
            }

        );
        const windowClickSubs = postal.subscribe(
            "app.menu.window.split.click",
            (envelope) => {
                handlePostalMenuWindowClick(envelope.payload);
            }
        );
        const pageSubs = postal.subscribe(
            "app.page.card.change",
            (envelope) => {
                setCard(envelope.payload);
            }
        );

        return () => {
            splitSubs();
            closeSubs();
            windowClickSubs();
            pageSubs();
        }
    }, []);

    useEffect(() => {
        
        if ( Array.isArray(modules) ) {
            console.log("INITIALIZING CARD LAYOUT");
            let amod = null;
            const pcards = [];
            modules.forEach( module => {
                module.setCard = setCard;
                if ( module.active ) {
                    amod = module;
                }
                if ( module.cardContent?.permanent) {
                    pcards.push(module.cardContent);
                    console.log('Adding permanent card: ' + module.cardContent.name);
                }
            });
            console.log('Found ' + modules.length + ' modules...');
            // we only set permanentcards at initialization to ensure they remain stable
            setPermanentCards(pcards);
            if ( amod && amod.cardContent ) {
                setCard(amod.cardContent);
            }
        }

    },[modules])

    useEffect( () => {
        if ( split ) {
            handleRightPanelClose();
        }
    },[activeModule]);

    useEffect(() => {
        console.log('CL: splitWindowEnabled: ' + splitWindowEnabled + ', useSplitWindow: ' + useSplitWindow);
        if ( useSplitWindow && splitWindowEnabled) {
            localSplit = true;
            setSplit(true);
            dispatch(setSplitMode(true));
            postal.publish('app.menu.window.split', { split: true });
        } else {
            localSplit = false;
            setSplit(false);
            dispatch(setSplitMode(false));
            postal.publish('app.menu.window.split', { split: false });
        }
    },[useSplitWindow,splitWindowEnabled]);

    const handlePostalSplitWindow = (data) => {
        if ( data ) {
            localSplit = data.enabled && data.active;
            setSplit(localSplit);
            dispatch(setSplitMode(localSplit));
            postal.publish('app.menu.window.split', { split: localSplit });
        }
    };

    const handlePostalCloseWindow = (data) => {

    };

    const handlePostalMenuWindowClick = (data) => {
        setWindowMenuAnchorEl(data.event?.currentTarget);
    };

    const handleWindowMenuClose = () => {
        setWindowMenuAnchorEl(null);
    };

    const handleWindowMenuCardClick = (card) => {
        // console.log('Split menu click: ' + JSON.stringify(card));
        setWindowMenuAnchorEl(null);
        if ( split && card /* && card.name === globalRightCard?.name */ ) {
            handleRightPanelClose();
            if ( card.name === globalRightCard?.name ) {
                return;
            }
        }
        const cc = getCurrentCard();
        if ( cc.name === card.name ) {
            /*
            globalLeftCard = getPreviousCard(card.name);
            setLeftCard(globalLeftCard);
            setLocalCard(globalLeftCard, true);
            */
            closeTopCard(card.name);
        } else {
            globalLeftCard = getCurrentCard();
            setLeftCard(globalLeftCard);
        }
        setRightCard(card);
        globalRightCard = card;
        
        localSplit = true;
        setSplit(true);
        dispatch(setSplitMode(true));
        postal.publish('app.menu.window.split', { split: true });
        if ( card.enableRightCloseButton ) {
            // console.log('Enabled right close: ' + card.name + ' : ' + card.enableRightCloseButton);
            setRightCloseEnabled(true);
        } else {
            setRightCloseEnabled(false);
        }
        if ( typeof card.onLoad === 'function') {
            card.onLoad(card.onLoadParams);
        }
        // in case some page is listening to window resize events
        setTimeout( () => 
            window.dispatchEvent(new Event('resize')), 200);
    };

    const handleWindowMenuSplitClose = (card) => {
        setSplit(false);
        localSplit = false;
        dispatch(setSplitMode(false));
        postal.publish('app.menu.window.split', { split: false });
    };

    const getCardZindex = useCallback((card) => {
        let r = '0';
        const currentCard = getCurrentCard();

        if ( card && split && (leftCard?.name === card?.name || rightCard?.name === card?.name)) {
            r = '20';
        }
        
        if ( card && !split && ( currentCard?.name === card?.name || leftCard?.name === card?.name) ) {
            r = '20';
        }
        
        // console.log('Zindex for card ' + card.name + ' = ' + r);
        return r;
    },[leftCard, rightCard, split, cardStack]);

    const getCardLeftPosition = useCallback((card) => {
        if ( split && card && rightCard?.name === card?.name) {
            if ( !rightCard.visible ) {
                const cm = { ...cardMap };
                permanentCards.forEach( c => {
                    const pc = {...c};
                    pc.visible = false;
                    cm[pc.name] = pc;
                });
                const rc = {...rightCard};
                rc.visible = true;
                cm[rc.name] = rc;
                setCardMap(cm);
            }
            return '50%';
        }
        return '0';
    },[split, rightCard, permanentCards]);

    const getBorderLeft = useCallback((card) => {
        if ( split && card && rightCard?.name === card?.name) {
            return '1px solid rgba(0,0,0,0.15)';
        }
        return 'none';
    },[split, rightCard, permanentCards]);

    const getCardWidth = useCallback((card) => {
        if ( split && (leftCard?.name === card?.name || rightCard?.name === card?.name)) {
            return '50%';
        }
        return '100%'
    },[split,leftCard,rightCard]);

    const getCurrentCard = useCallback(() => {
        if ( globalCardStack.length > 0 ) {
            return globalCardStack[globalCardStack.length - 1];
        }
    },[cardStack]);

    const renderedPermanentCards = useMemo( () => {
        return (
            Array.isArray(permanentCards) && permanentCards.map( card => 
                <Box id={`PCARD0_${card.name}`}  key={`PCARD0_${card.name}`} sx={{position: 'absolute', top: '0', left: getCardLeftPosition(card), width: getCardWidth(card), height: '100%', 
                    overflowX: 'clip', backgroundColor: 'white', zIndex: getCardZindex(card), borderLeft: getBorderLeft(card)}} >{card.page}</Box>
            )
        );
    }, [permanentCards, leftCard, rightCard, cardStack, split]);

    const renderedCards = useMemo( () => {
        return (Array.isArray(cards) && cards.map( card => 
                <Box id={card.key} key={card.key} sx={{position: 'absolute', top: '0', left: getCardLeftPosition(card), width: getCardWidth(card), height: '100%', 
                    overflowX: 'clip', backgroundColor: 'white', zIndex: getCardZindex(card), borderLeft: getBorderLeft(card)}}>{card.page}</Box>)
                );
    },[cards, leftCard, rightCard, cardStack, split]);

    /**
     * Push to activeRightCards
     * @param {*} card 
     */
    const pushRightCard = (card) => {
        // console.log('Pushing right card: ' + JSON.stringify(card));
        const ac = globalActiveRightCards.find( c => c.name === card.name);
        if ( !ac ) {
            globalActiveRightCards.push({
                name: card.name, 
                label: card.label,
                icon: card.icon, 
                right: card.right, 
                reuse: card.reuse, 
                permanent: card.permanent,
                enableRightCloseButton: card.enableRightCloseButton,
                includeModules: card.includeModules,
                origin: card.origin,
                closeCallback: card.closeCallback,
                closeNotify: card.closeNotify,
                onLoad: card.onLoad,
                onLoadParams: card.onLoadParams,
            });
        } else {
            const aci = globalActiveRightCards.findIndex( c => c.name === card.name);
            if ( aci >= 0 ) {
                globalActiveRightCards[aci].label = card.label;
                globalActiveRightCards[aci].onLoad = card.onLoad;
                globalActiveRightCards[aci].onLoadParams = card.onLoadParams;
            }
        }
        if ( globalActiveRightCards.length > 0 ) {
            dispatch(setSplitWindow({enabled: true, useSplitWindow: false}));
        }
    };

    

    const pushCard = (card) => {
        // const cs = [...cardStack];
        const si = globalCardStack.findIndex(c => c.name === card.name);
        if (si >= 0) {
            globalCardStack.splice(si, 1);
        }
        /*
        globalCardStack.push({
            name: card.name, 
            label: card.label,
            icon: card.icon,
            reuse: card.reuse,
            right: card.right,
            permanent: card.permanent,
        });
        */
        globalCardStack.push(card);
        setCardStack(globalCardStack);
        // console.log('PUSHED card: ' + card.name +  ', origin= ' + card.origin +  ' stack  = ' + JSON.stringify(globalCardStack.map(c => c.name)));
    };

    const getPreviousCard = (fromCardName) => {
        if ( globalCardStack.length > 1 ) {
            let tcard = globalCardStack[globalCardStack.length - 2];
            if ( fromCardName && fromCardName === tcard?.name ) {
                tcard = globalCardStack[globalCardStack.length - 1];
            }
            return tcard;
        }
    };

    const closeTopCard = (xname) => {
        // console.log('closing card: ' + xname + ' stack = ' + JSON.stringify(cardStack.map(c => c.name)));
        const target = getPreviousCard(xname);
        if (target) {
            const r = globalCardStack.pop();
            if ( typeof r?.closeNotify === 'function' ) {
                r.closeNotify(true);
            } else {
                console.log('closeNotify: ' + r?.name + ' : ' + (typeof r?.closeNotify));
            }
            setCardStack(globalCardStack);
            // console.log('Reverting to ' + target.name);
            setLocalCard(target, true); // and reuse the state
        }
    }

    const setCard = (card) => {
        console.log('setCard: ' + card.cmd + ' : ' + card.name);
        if ( card.cmd === 'NACT') {
            // no action
        } else if ( card.cmd === 'PUSH_RIGHT') {
            // console.log("PUSH_RIGHT: " + JSON.stringify(card));
            pushRightCard(card);
        } else if ( card.cmd === 'CLOSE_WINDOW') {
            if ( localSplit && globalRightCard?.name === card.name) {
                // TODO: need to check if this is comming from left panel and only set top of stack
                setSplit(false);
                localSplit = false;
                dispatch(setSplitMode(false));
                postal.publish('app.menu.window.split', { split: false });
                console.log('left card : ' + globalLeftCard.name + ', origin: ' + globalLeftCard.origin);
                if ( globalLeftCard?.origin === card.name) {
                    // the left card was opened by the right card, we should close it also!
                    closeTopCard(globalLeftCard.name);
                }
            } else {
                // special case to set the previous card in the stack, not the top
                closeTopCard(card.name);
            }
        } else {
            setLocalCard(card);
        }
        
    };

    const setLocalCard = (card, reuse = false) => {
        // console.log('Set local card: ' + card.name);
        const pindex = permanentCards.findIndex(c => c.name === card.name);
        if (pindex >= 0 || card.permanent) {
            const cm = { ...cardMap };
            permanentCards.forEach((c, i) => {
                if (i !== pindex) {
                    c.visible = false;
                    cm[c.name] = card;
                }
            });
            
            if ( card.right ) {
                pushRightCard(card);
            }
            setLeftCard(card);
            globalLeftCard = card;
            // console.log('Found permanent card: ' + JSON.stringify(card));
            card.visible = true;
            cm[card.name] = card;
            setCardMap(cm);
            
            pushCard(card);
        } else {
            // const crds = [...cards];
            const index = globalCards.findIndex(c => c.name === card.name);
            if (index >= 0) {
                globalCards[index].reuse = card.reuse;
                globalCards[index].right = card.right;
                globalCards[index].origin = card.origin;
                globalCards[index].closeCallback = card.closeCallback;
                globalCards[index].closeNotify = card.closeNotify;
                if (!card.reuse && !reuse) {
                    if ( !globalCards[index].sequence ) {
                        globalCards[index].sequence = 1;
                    } else {
                        globalCards[index].sequence = globalCards[index].sequence + 1;
                    }
                    globalCards[index].key = `RCARD${globalCards[index].sequence}_${card.name}`;
                    globalCards[index].page = card.page;
                }
                setCards(globalCards);
            } else {
                if ( !card.sequence ) {
                    card.sequence = 1;
                } else {
                    card.sequence = card.sequence + 1;
                }
                card.key = `RCARD${card.sequence}_${card.name}`;
                globalCards.push(card);
                setCards(globalCards);
            }
            const cm = { ...cardMap };
            // console.log('Split window at setCard = ' + localSplit);
            if (!localSplit) {
                permanentCards.forEach(c => {
                    c.visible = false;
                    cm[c.name] = {
                        name: c.name, 
                        permanent: c.permanent, 
                        reuse: c.reuse, 
                        right: c.right, 
                        icon: c.icon, 
                        label: c.label,
                        origin: c.origin,
                        closeCallback: c.closeCallback,
                        closeNotify: c.closeNotify,
                    };
                });
            }
            const ccard = { 
                name: card.name,
                permanent: card.permanent,
                reuse: card.reuse,
                right: card.right,
                icon: card.icon,
                label: card.label,
                enableRightCloseButton: card.enableRightCloseButton,
                includeModules: card.includeModules,
                origin: card.origin,
                closeCallback: card.closeCallback,
                closeNotify: card.closeNotify,
            };
            cm[card.name] = ccard;
            setCardMap(cm);
            
            setLeftCard(ccard);
            globalLeftCard = ccard;
            if ( ccard.right ) {
                pushRightCard(ccard);
            }
            // last card in stack is most recent
            pushCard(card);
        }
    };

    const commitRightPanelClose = (closeStack=[]) => {
        setSplit(false);
        localSplit = false;
        dispatch(setSplitMode(false));
        setRightCloseEnabled(false);
        postal.publish('app.menu.window.split', { split: false });
        // in case some page is listening to window resize events
        setTimeout( () => window.dispatchEvent(new Event('resize')), 200);
        for (let i = 0; i < closeStack.length; i++) {
            console.log('Closing top card ' + closeStack[i].name);
            closeTopCard(closeStack[i].name);
        }
    }

    const handleRightPanelClose = () => {
        /*
        setSplit(false);
        localSplit = false;
        dispatch(setSplitMode(false));
        setRightCloseEnabled(false);
        postal.publish('app.menu.window.split', { split: false });
        // in case some page is listening to window resize events
        setTimeout( () => window.dispatchEvent(new Event('resize')), 200);
        */
        // console.log('left card : ' + globalLeftCard.name +  ', right card: ' + globalRightCard.name +  ', origin: ' + globalLeftCard.origin);
        // follow the ancestors to check if it was opened by the right panel card
        let ocard = globalLeftCard;
        let origin = globalLeftCard?.origin;
        let doClose = false;
        const closeStack = [];
        const seen = {};
        do {
            if (origin === globalRightCard?.name) {
                // the left card was opened by the right card, we should close it also!
                closeStack.push(ocard);
                doClose = true;
                break;
            } else {
                // console.log('Finding origin: ' + origin );
                closeStack.push(ocard);
                ocard = globalCardStack.find(c => c.name === origin);
                if ( ocard ) {
                    // console.log('Found origin: ' + ocard.name);
                    origin = ocard.origin;
                } else {
                    break;
                }
            }
            if (origin) {
                if (seen[origin]) {
                    // failsafe to avoid loop forever
                    break;
                }
                seen[origin] = true;
            }
        } while (origin);
        // console.log('Right close: ' + doClose + ' doCloseStack = ' + JSON.stringify(closeStack.map( c => c.name)) );
        if (doClose) {
            let total = true;
            closeStack.reduce((chain, currentCard) => {
                return chain.then((value) => {
                    total = total && value;
                    // abort subsequent calls when the first one fails
                    if ( total ) {
                        if ( typeof currentCard.closeCallback === 'function') {
                            return currentCard.closeCallback();
                        }
                    }
                    return true;
                }, (error) => {
                    total = false;
                    return false;
                });
            }, Promise.resolve(true))
                .then(() => {
                    // console.log("All callbacks finished in order")
                    // if any fails, close operation is aborted
                    if ( total ) {
                        commitRightPanelClose(closeStack);
                    } else {
                        console.log('aborting close operation...');
                    }
                }).catch(error => {
                        console.log('aborting close operation...');
                });
            /*
            for (let i = 0; i < closeStack.length; i++) {
                console.log('Closing top card ' + closeStack[i].name);
                closeTopCard(closeStack[i].name);
            }
            */
        } else {
            commitRightPanelClose();
        }
    };

    const isCardModuleValid = (card) => {
        if ( Array.isArray(card.includeModules) ) {
            return card.includeModules.includes(activeModule);
        }
        return true;
    }
    /*
        visibility: cardMap[card.name]?.visible ? 'visible' : 'hidden'
    */

    return (
        <Box sx={{position: 'relative', width: '100%', height: '100%'}}>
            {renderedPermanentCards}
            {renderedCards}
            <Tooltip title="Close">
                <IconButton onClick={handleRightPanelClose} sx={{position: 'absolute', top: '0.75em', right: '0.6em', zIndex: 40, 
                    visibility: rightCloseEnabled ? 'visible' : 'hidden' }}>
                    <CloseIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={windowMenuAnchorEl}
                id="app-menu"
                open={openWindowMenu}
                onClose={handleWindowMenuClose}
                onClick={handleWindowMenuClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ padding: '0.5ex', paddingLeft: '1.5em', paddingRight: '1em', width: '20em', minHeight: '18em', display: 'flex', 
          flexWrap: 'wrap', justifyContent: 'left', alignItems: 'flex-start'  }}>
                    {globalActiveRightCards.map((rcard, index) => {
                        return (
                            <MenuItem key={'rcard' + index} onClick={() => handleWindowMenuCardClick(rcard)}
                                sx={{ width: "8.5em", display: 'block' }}
                                disabled={!isCardModuleValid(rcard)}
                                 >
                                <ListItemIcon sx={{ display: "block", textAlign: "center", fontSize: "2.8em" }}>
                                    {rcard.icon}
                                </ListItemIcon>
                                <Typography align="center" sx={{ fontSize: '12px', padding: 0, width: '8.5em', whiteSpace: 'normal'}}>{rcard.label}</Typography>
                            </MenuItem>
                        );
                    }
                    )}
                </Box>
            </Menu>
        </Box>
    );
}

export { CardLayout };